import { useMemo } from "react"
import { BarStack } from "@visx/shape"

import { scaleBand, scaleOrdinal } from "@visx/scale"

import { localPoint } from "@visx/event"
import { useI18n } from "providers/I18n"

import { useRevenueParams } from "../../../hooks/useRevenueParams"
import { formatData, getTitles, getRegionTitle, sumDirection } from "./utils"

import { getPalette } from "../palette"
import { netFormat, percentanceFormat } from "../../../helpers/formatters"
import XAxisSub from "./XAxisSub"
import PosValuesAxis from "./PosValuesAxis"
import GlobalsAxis from "./GlobalsAxis"
import { PositionScale } from "@visx/shape/lib/types"
import Pill from "./Pill"

export type TooltipData = {
  bar: {
    key: string
    index: number
    height: number
    width: number
    x: number
    y: number
    color: string
  }
  hoveredIndex: number
  showSecondBar?: boolean
}

export type BarStackProps = {
  width: number
  height: number
  search: string
  entity: string
  tableData: any
  tbgNetKeys: string[]
  tbgOibiaKeys: string[]
  dataType: "gcf" | "non-gcf"
  availableEntities: string[][]
  entityIndex: number
  isExpanded?: boolean
  YScale: PositionScale
  defaultConfig: {
    top: number
    left: number
    doubleBarPosition: number
  }
  showSecondBar: boolean
  showTooltip: (value: any) => void
  hideTooltip: () => void
  hasAvailableEntities: boolean
}

export default function TBGBarChart({
  width,
  height,
  search,
  entity,
  tableData,
  tbgNetKeys,
  tbgOibiaKeys,
  dataType,
  availableEntities,
  entityIndex,
  isExpanded,
  YScale,
  defaultConfig,
  showSecondBar,
  showTooltip,
  hideTooltip,
  hasAvailableEntities,
}: BarStackProps) {
  const { t } = useI18n()
  const { regionParam } = useRevenueParams()

  const commonProps = useMemo(() => {
    return {
      data: tableData,
      search,
      entity,
      regionParam,
      t,
      availableEntities,
      entityIndex,
    }
  }, [
    entity,
    regionParam,
    search,
    t,
    tableData,
    availableEntities,
    entityIndex,
  ])

  const formatedTotalTbgRevenueData = useMemo(() => {
    return tbgNetKeys?.map((key) =>
      formatData(key, { ...commonProps, isGlobal: true }, dataType),
    )
  }, [commonProps, dataType, tbgNetKeys])

  const tbgRevenueGlobals = formatedTotalTbgRevenueData?.map(getTitles)

  const formatedTbgOIData = useMemo(() => {
    return tbgOibiaKeys?.map((key) => formatData(key, commonProps, dataType))
  }, [commonProps, dataType, tbgOibiaKeys])

  const formatedTbgNetData = useMemo(() => {
    return tbgNetKeys?.map((key) =>
      formatData(key, commonProps, dataType, "tbg"),
    )
  }, [commonProps, dataType, tbgNetKeys])

  const tbgKeys = Object.keys(formatedTbgNetData?.[0]).filter(
    (d) => d !== "title",
  )
  const updatedKeys = [getRegionTitle(entity, regionParam)].concat(tbgKeys)

  const tbgTotals = formatedTbgNetData?.reduce((ret, cur) => {
    const pos = sumDirection(tbgKeys, cur, 1)
    const neg = sumDirection(tbgKeys, cur, -1)
    ret.push({ pos, neg })
    return ret
  }, [])

  const maxTBGTotals = tbgTotals.map((t: { pos: number; neg: number }) => t.pos)

  const globalsTBGRevenueScale = useMemo(
    () =>
      scaleBand<any>({
        domain: [
          `${netFormat(tbgRevenueGlobals?.[0])}-1`,
          `${netFormat(tbgRevenueGlobals?.[1])}-2`,
          `${netFormat(tbgRevenueGlobals?.[2])}-3`,
        ],
        padding: 0.6,
        range: [defaultConfig.left, width],
      }),
    [tbgRevenueGlobals, defaultConfig.left, width],
  )

  const topPosTBGRevenueValuesScale = useMemo(
    () =>
      scaleBand<any>({
        domain: [
          `${maxTBGTotals[0] === 0 ? "" : netFormat(maxTBGTotals[0])}-1`,
          `${maxTBGTotals[1] === 0 ? "" : netFormat(maxTBGTotals[1])}-2`,
          `${maxTBGTotals[2] === 0 ? "" : netFormat(maxTBGTotals[2])}-3`,
        ],
        padding: 0.6,
        range: [defaultConfig.left, width],
      }),
    [maxTBGTotals, defaultConfig.left, width],
  )

  const bottomTitlesScale = useMemo(
    () =>
      scaleBand<string>({
        domain: formatedTbgNetData.map(getTitles),
        padding: 0.6,
        range: [defaultConfig.left, width],
      }),
    [defaultConfig.left, formatedTbgNetData, width],
  )

  const bottomSubTBGRevenueTitlesScale = useMemo(
    () =>
      scaleBand<string>({
        domain: [
          `${t("revenue.plain.tbg")}-${t("revenue.revenue")}-1`,
          `${t("revenue.plain.tbg")}-${t("revenue.revenue")}-2`,
          `${t("revenue.plain.tbg")}-${t("revenue.revenue")}-3`,
        ],
        padding: 0.6,
        range: [defaultConfig.left, width],
      }),
    [t, defaultConfig.left, width],
  )

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: updatedKeys,
        range: getPalette(entity),
      }),
    [entity, updatedKeys],
  )

  return (
    <>
      <BarStack
        key={entityIndex}
        data={formatedTbgNetData || []}
        keys={tbgKeys}
        x={getTitles}
        xScale={bottomTitlesScale}
        yScale={YScale}
        color={colorScale}
        offset="diverging"
      >
        {(barStacks) => {
          return barStacks.map((barStack, i) => {
            return (
              <g key={i}>
                {barStack.bars.map((bar, index) => {
                  const calculatedX =
                    bar.x + defaultConfig.doubleBarPosition + bar.width / 2
                  const formatedOIValue = percentanceFormat(
                    formatedTbgOIData?.[bar.index][bar.key],
                  )
                  return (
                    <g>
                      <rect
                        key={`bar-stack-${barStack.index}-${index}`}
                        x={calculatedX}
                        y={bar.y + defaultConfig.top}
                        height={bar.height}
                        width={bar.width}
                        fill={getPalette(entity)[barStack.index]}
                        opacity={0.4}
                        onMouseLeave={() => hideTooltip()}
                        onMouseMove={(event) => {
                          const eventSvgCoords = localPoint(event)
                          const left = bar.x + bar.width / 2
                          showTooltip({
                            tooltipData: {
                              bar,
                              hoveredIndex: index,
                              showSecondBar: true,
                            },
                            tooltipTop: eventSvgCoords?.y,
                            tooltipLeft: left,
                          })
                        }}
                      />

                      {isExpanded && bar.height > 30 && (
                        <Pill
                          calculatedX={calculatedX}
                          barstackIndex={barStack.index}
                          bar={bar}
                          top={defaultConfig.top}
                          formatedOIData={formatedTbgOIData}
                          formatedOIValue={formatedOIValue}
                          dataType={dataType}
                          showSecondBar={showSecondBar}
                        />
                      )}
                    </g>
                  )
                })}
              </g>
            )
          })
        }}
      </BarStack>

      <>
        {hasAvailableEntities && (
          <>
            <XAxisSub
              scale={bottomSubTBGRevenueTitlesScale}
              top={isExpanded ? height / 1.195 : height / 1.185}
              x={18.5}
              chartWidth={width}
              isMultiText
            />
            <GlobalsAxis
              scale={globalsTBGRevenueScale}
              top={[
                YScale(maxTBGTotals[0])!,
                YScale(maxTBGTotals[1])!,
                YScale(maxTBGTotals[2])!,
              ]}
              x={18.5}
              chartWidth={width}
            />
          </>
        )}
        <PosValuesAxis
          scale={topPosTBGRevenueValuesScale}
          top={[
            YScale(maxTBGTotals[0])!,
            YScale(maxTBGTotals[1])!,
            YScale(maxTBGTotals[2])!,
          ]}
          x={18.5}
          chartWidth={width}
        />
      </>
    </>
  )
}
